import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium/DataGridPremium';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TopPerformersRecord } from 'common/models/Reporting/BusinessIntelligence';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import RWTextFieldComponent from 'components/RWTextFieldComponent';
import MultipleUserPicker from 'components/Pickers/MultipleUserPicker';
import SettingsIcon from '@mui/icons-material/Settings';
import { NameIdObj } from 'common/models/GenericTypes';
import MultipleGroupPicker from 'components/Pickers/MultipleGroupPicker';
import { TopPerformersFilter } from 'common/models/Reporting/AnalyticsFilterData';
import { CustomTopPerformers } from 'services/ReportingService';

interface Props {
  id: string;
  title: string;
  sourceType: string;
  userFilterIds: string;
  reportPeriod: number;
  noOfTopConsultants: number;
  showConfigButton?: boolean;
  kioskToken?: string | null;
  refreshFlag?: boolean | null;
  saveConfigHandler?: (
    id: string,
    title: string,
    userFilterIds: string,
    sourceType: string,
    reportPeriod: number,
    noOfTopConsultants: number
  ) => void;
}

interface CustomTopPerformersRecordWithId extends TopPerformersRecord {
  id: number;
}
export default function TopPerformersTableGraph({
  id,
  title: titleProp,
  sourceType: sourceTypeProps,
  userFilterIds: userIdsProps,
  reportPeriod: reportPeriodProp,
  noOfTopConsultants: noOfTopConsultantsProp,
  showConfigButton = true,
  kioskToken = null,
  refreshFlag = null,
  saveConfigHandler,
}: Props) {
  const [title, setTitle] = useState('');
  const [errorHandler, setErrorHandler] = useState('');
  const [sourceType, setSourceType] = useState('currentuser');
  const [reportingPeriod, setReportingPeriod] = useState(1);
  const [noOfTopConsultants, setNoOfTopConsultants] = useState(3);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [topPerformerData, setTopPerformerData] = useState<
    CustomTopPerformersRecordWithId[] | null
  >(null);

  useEffect(() => {
    setTitle(titleProp);
    if (reportPeriodProp) {
      setReportingPeriod(reportPeriodProp);
    }

    setSourceType(sourceTypeProps);
    if (noOfTopConsultantsProp) {
      setNoOfTopConsultants(noOfTopConsultantsProp);
    }

    if (userIdsProps) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(userIdsProps.split(',').map(Number));
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(userIdsProps.split(',').map(Number));
      }
    }
  }, [sourceTypeProps, userIdsProps, reportPeriodProp, titleProp, noOfTopConsultantsProp]);

  const getRevenueData = async () => {
    setErrorHandler('');
    setIsLoading(true);
    let filterTypeId = 0;
    if (sourceType == 'consultants') {
      filterTypeId = 4;
    } else if (sourceType == 'groups') {
      filterTypeId = 3;
    } else if (sourceType == 'currentuser') {
      filterTypeId = 5;
    } else {
      filterTypeId = 1;
    }
    const filter = {
      reportPeriod: reportingPeriod,
      userFilterID: userIdsProps,
      userFilterTypeID: filterTypeId,
      noOfTopConsultants: noOfTopConsultants,
    } as TopPerformersFilter;
    const res = await CustomTopPerformers(filter, kioskToken, setErrorHandler);
    if (res) {
      setTopPerformerData(res.map((r, i) => ({ ...r, id: i })));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    !showConfigDialog && getRevenueData();
  }, [sourceType, reportingPeriod, noOfTopConsultants, showConfigDialog, kioskToken]);

  useEffect(() => {
    if (refreshFlag && kioskToken) {
      getRevenueData();
    }
  }, [sourceType, refreshFlag, kioskToken]);

  const mapIdsToString = useCallback(() => {
    let selectedIds = '';
    if (sourceType == 'consultants') {
      selectedIds = selectedUsers.join(',');
    } else if (sourceType == 'groups') {
      selectedIds = selectedGroups.join(',');
    }
    return selectedIds;
  }, [selectedUsers, selectedGroups, sourceType]);

  const onSaveConfig = useCallback(() => {
    saveConfigHandler &&
      saveConfigHandler(
        id,
        title,
        mapIdsToString(),
        sourceType,
        reportingPeriod,
        noOfTopConsultants
      );
    setShowConfigDialog(false);
  }, [
    saveConfigHandler,
    selectedUsers,
    selectedGroups,
    sourceType,
    title,
    reportingPeriod,
    id,
    noOfTopConsultants,
  ]);

  const sourceTypeChangeHandler = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSourceType(value);
  }, []);

  const onNumericValuesChange = useCallback((values: NameIdObj[], type: string) => {
    const ids = values.map((v) => v.id);
    if (type == 'consultants') {
      setSelectedUsers(ids);
    }

    if (type == 'groups') {
      setSelectedGroups(ids);
    }
  }, []);

  const columns = useMemo<GridColDef[]>(() => {
    const tooltipRenderer = (params: GridRenderCellParams) => {
      if (params.colDef.field === 'total')
        return <div title={params.value}>{(params.value as number).toLocaleString()}</div>;
      return <div title={params.value}>{params.value}</div>;
    };

    return [
      {
        field: 'consultant',
        headerName: 'Consultant',
        headerAlign: 'center',
        disableColumnMenu: true,
        flex: 1,
        renderCell: tooltipRenderer,
      },
      {
        field: 'total',
        headerName: 'Total',
        headerAlign: 'center',
        align: 'right',
        disableColumnMenu: true,
        flex: 1,
        renderCell: tooltipRenderer,
      },
    ];
  }, []);

  const handleCancel = () => {
    setShowConfigDialog(false);
    setTitle(titleProp);
    if (reportPeriodProp) {
      setReportingPeriod(reportPeriodProp);
    }

    setSourceType(sourceTypeProps);
    if (noOfTopConsultantsProp) {
      setNoOfTopConsultants(noOfTopConsultantsProp);
    }

    if (userIdsProps) {
      if (sourceTypeProps == 'consultants') {
        setSelectedUsers(userIdsProps.split(',').map(Number));
      }

      if (sourceTypeProps == 'groups') {
        setSelectedGroups(userIdsProps.split(',').map(Number));
      }
    }
  };

  return (
    <>
      <Dialog open={showConfigDialog} fullWidth>
        <DialogTitle>Configure Element</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt="10px">
            <RWTextFieldComponent
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              select
              label="Source Type"
              value={sourceType}
              onChange={sourceTypeChangeHandler}
              fullWidth
            >
              <MenuItem value="company">All</MenuItem>
              <MenuItem value="consultants">Consultant</MenuItem>
              <MenuItem value="currentuser">Current User</MenuItem>
              <MenuItem value="groups">Group</MenuItem>
            </TextField>
            {sourceType === 'consultants' && (
              <MultipleUserPicker
                value={selectedUsers}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'consultants');
                }}
                label="Consultants"
              />
            )}
            {sourceType === 'groups' && (
              <MultipleGroupPicker
                value={selectedGroups}
                onSelect={(s) => {
                  onNumericValuesChange(s, 'groups');
                }}
                label="Groups"
              />
            )}
            <TextField
              select
              label="Reporting Period"
              value={reportingPeriod.toString()}
              onChange={({ target }) => setReportingPeriod(+target.value)}
              fullWidth
            >
              <MenuItem value="1">This Month</MenuItem>
              <MenuItem value="2">This Quarter (Financial)</MenuItem>
              <MenuItem value="3">This Year (Financial)</MenuItem>
              <MenuItem value="4">Last Month (Financial)</MenuItem>
              <MenuItem value="5">Last Quarter (Financial)</MenuItem>
              <MenuItem value="6">Last Year (Financial)</MenuItem>
            </TextField>
            <TextField
              select
              label="Number Of Top Consultants"
              value={noOfTopConsultants.toString()}
              onChange={({ target }) => setNoOfTopConsultants(+target.value)}
              fullWidth
            >
              <MenuItem value="3">Top 3</MenuItem>
              <MenuItem value="5">Top 5</MenuItem>
              <MenuItem value="10">Top 10</MenuItem>
              <MenuItem value="15">Top 15</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={() => handleCancel()}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={onSaveConfig}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showConfigButton && (
        <IconButton
          sx={{
            position: 'absolute',
            right: 30,
            zIndex: (t) => t.zIndex.appBar - 5,
            color: (t) => t.palette.text.disabled,
          }}
          onClick={() => setShowConfigDialog(true)}
        >
          <SettingsIcon />
        </IconButton>
      )}
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" padding={1} textAlign="center" fontWeight={600} component="div">
            {title}
          </Typography>
          {isLoading && errorHandler == '' && (
            <Box m="auto">
              <CircularProgress />
            </Box>
          )}
          {!isLoading && topPerformerData != null && errorHandler == '' && (
            <DataGridPremium
              density="compact"
              rows={topPerformerData}
              columns={columns}
              disableRowSelectionOnClick
              hideFooter
            />
          )}
        </div>
        {errorHandler != '' && (
          <Typography
            variant="subtitle2"
            padding={1}
            textAlign="center"
            fontWeight={600}
            component="div"
          >
            {errorHandler}
          </Typography>
        )}
      </Paper>
    </>
  );
}
